import { AuthContext } from 'providers/authorization-context';
import { massUpdateActions } from 'constants/MassUpdates';
import { REGIONS } from 'constants/Regions';
import { isProd } from 'helpers/helper_functions';
import React, { useContext, useMemo, useRef, useState } from 'react';
import {
  firstLetterTitlization,
  isAuthorizedToRender,
} from 'utils/localization/utils';
import BulkEditModal from 'components/modules/shipments/MassUpdateModal';
import ChangeUserModal from 'components/modules/shipments/ChangeUserModal';
import BulkUpdateSendDOModal from 'components/modules/shipments/BulkUpdateSendDOModal';
import BulkUpdateCarrierCostModal from 'components/modules/shipments/BulkUpdateCarrierCostModal';
import BulkAddAccessorialsModal from 'components/modules/shipments/accounting/BulkAddAccessorialsModal';
import BulkGenerateRateConfirmation from 'components/modules/shipments/BulkGenerateRateConfirmationModal';

import BulkAddYardsModal from 'components/modules/yards/BulkAddYardsModal';
import AssignDriverModal from 'components/modules/shipments/AssignDriverModal';
import { ConfirmationModal } from 'components/v2/confirmation-modal';
import {
  evaluateBillingStatus,
  bulkCloseJobs,
} from 'utils/ShipmentWebAPIUtils';
import useModal from 'hooks/useModal';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@cargomatic/ui';
import {
  ChevronDown,
  CircleDollarSign,
  Layers,
  RefreshCw,
  Send,
  UserPlus,
} from 'lucide-react';
import { showToast } from 'stores/ToastStore';
import SessionStore from 'stores/SessionStore';
import BulkBOLGenerationModal from '../shipments/BulkBOLGenerationModel';
import ConfirmAssignCarrierModal from '../shipments/ConfirmAssignCarrierModal';
import { OverrideBillingStatusModal } from '../shipments/OverrideBillingStatusModal';
import { BillingStatusPendingReviewModal } from '../shipments/BillingStatusPendingReviewModal';
import BulkUploadDocumentsModel from '../shipments/BulkUploadDocumentsModel';
import { PENDING_REVIEW } from 'constants/billing-status';
import _ from 'underscore';
import UpdatePrenoticeShipmentStatusModal from '../shipments/UpdatePrenoticeShipmentStatusModal';
import BulkShipmentBridges from '../shipments/BulkShipmentBridges';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { titleize } from 'underscore.string';

const CATEGORIES = {
  ASSIGNMENTS: 'assignments',
  DATE_STATUS: 'date_status',
  BILLING: 'billing',
  SEND: 'send',
  BULK: 'bulk_operations',
};

const CATEGORY_ORDER = [
  CATEGORIES.ASSIGNMENTS,
  CATEGORIES.DATE_STATUS,
  CATEGORIES.BILLING,
  CATEGORIES.SEND,
  CATEGORIES.BULK,
];

const CATEGORY_LABELS = {
  [CATEGORIES.ASSIGNMENTS]: 'Assignments',
  [CATEGORIES.DATE_STATUS]: 'Date & Status Updates',
  [CATEGORIES.BILLING]: 'Billing',
  [CATEGORIES.SEND]: 'Send & Notifications',
  [CATEGORIES.BULK]: 'Bulk Operations',
};

const CATEGORY_ICONS = {
  [CATEGORIES.ASSIGNMENTS]: UserPlus,
  [CATEGORIES.DATE_STATUS]: RefreshCw,
  [CATEGORIES.BILLING]: CircleDollarSign,
  [CATEGORIES.SEND]: Send,
  [CATEGORIES.BULK]: Layers,
};

const CATEGORY_ICON_TINTS = {
  [CATEGORIES.ASSIGNMENTS]: 'bg-teal-100 text-teal-700',
  [CATEGORIES.DATE_STATUS]: 'bg-emerald-100 text-emerald-700',
  [CATEGORIES.BILLING]: 'bg-amber-100 text-amber-700',
  [CATEGORIES.SEND]: 'bg-sky-100 text-sky-700',
  [CATEGORIES.BULK]: 'bg-violet-100 text-violet-700',
};

const CATEGORY_TAB_ICON_COLORS = {
  [CATEGORIES.ASSIGNMENTS]: 'text-teal-600',
  [CATEGORIES.DATE_STATUS]: 'text-emerald-600',
  [CATEGORIES.BILLING]: 'text-amber-600',
  [CATEGORIES.SEND]: 'text-sky-600',
  [CATEGORIES.BULK]: 'text-violet-600',
};

function ActionItem({ action, index, onRun }) {
  // cmdk fires onSelect on click in browsers, but its handler relies on
  // PointerEvents which jsdom doesn't support — tests trigger selection via
  // mousedown instead. The justFiredRef guard prevents the browser path
  // from running select() twice (once in mousedown, once in onSelect).
  const justFiredRef = useRef(false);
  const select = () => {
    if (action.disabled) return;
    if (justFiredRef.current) return;
    justFiredRef.current = true;
    queueMicrotask(() => {
      justFiredRef.current = false;
    });
    onRun(action);
  };
  const item = (
    <CommandItem
      item={action.id}
      index={index}
      disabled={action.disabled}
      onSelect={select}
      onMouseDown={(e) => {
        e.preventDefault();
        select();
      }}
      data-testid={action.dataTestId}
    >
      {action.label}
    </CommandItem>
  );

  if (action.disabled) {
    if (action.disabledText) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-not-allowed">{item}</span>
          </TooltipTrigger>
          <TooltipContent side="left">{action.disabledText}</TooltipContent>
        </Tooltip>
      );
    }
    return <span className="cursor-not-allowed">{item}</span>;
  }

  return item;
}

function BulkActionsDropdown({
  view,
  disabled,
  arrivedActionDisabled,
  changeShipperDisabled,
  changeCarrierDisabled,
  selectedShipmentsForAccessorials,
  selectedShipmentsForYards,
  selectedShipments,
  selectedRows,
  onBillingStatusPendingReviewUpdate,
  bulkUploadDocumentsDisabled,
  isSameShipperOrMasterShipper,
}) {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const [bulkAction, setBulkAction] = useState('');
  const [brokerCarrierDriver, setBrokerCarrierDriver] = useState();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');

  const {
    visible: changeShipperModalVisible,
    show: showChangeShipperModal,
    close: closeChangeShipperModal,
  } = useModal();

  const {
    visible: changeCarrierModalVisible,
    show: showChangeCarrierModal,
    close: closeChangeCarrierModal,
  } = useModal();

  const {
    visible: assignDriverModalVisible,
    show: showAssignDriverModal,
    close: closeAssignDriverModal,
  } = useModal();

  const {
    visible: confirmAssignCarrierModalVisible,
    show: showConfirmAssignCarrierModal,
    close: closeConfirmAssignCarrierModal,
  } = useModal();

  const {
    visible: changeConsigneeModalVisible,
    show: showChangeConsigneeModal,
    close: closeChangeConsigneeModal,
  } = useModal();

  const {
    visible: bulkAddAccessorialModalVisible,
    show: showBulkAddAccessorialModal,
    close: closeBulkAddAccessorialModal,
  } = useModal();

  const {
    visible: bulkUpdateCarrierCostModalVisible,
    show: showBulkUpdateCarrierCostModal,
    close: closeBulkUpdateCarrierCostModal,
  } = useModal();

  const {
    visible: bulkUpdateSendDOModalVisible,
    show: showBulkUpdateSendDOModal,
    close: closeBulkUpdateSendDOModal,
  } = useModal();

  const {
    visible: bulkBOLGenerationModalVisible,
    show: showBulkBOLGenerationModal,
    close: closeBulkBOLGenerationModal,
  } = useModal();

  const {
    visible: sendRateConfirmationModalVisible,
    show: showSendRateConfirmationModal,
    close: closeSendRateConfirmationModal,
  } = useModal();

  const {
    visible: bulkAddYardsModalVisible,
    show: showBulkAddYardsModal,
    close: closeBulkAddYardsModal,
  } = useModal();

  const {
    visible: evaluateBillingStatusConfirmationModalVisible,
    show: showEvaluateBillingStatusConfirmationModal,
    close: closeEvaluateBillingStatusConfirmationModal,
  } = useModal();

  const {
    visible: bulkEditModalVisible,
    show: showBulkEditModal,
    close: closeBulkEditModal,
  } = useModal();

  const {
    visible: bulkCloseModalVisible,
    show: showBulkCloseModal,
    close: closeBulkCloseModal,
  } = useModal();

  const {
    visible: overrideBillingStatusModalVisible,
    show: showOverrideBillingStatusModal,
    close: closeOverrideBillingStatusModal,
  } = useModal();

  const {
    visible: markBillingStatusPendingReviewModalVisible,
    show: showMarkBillingStatusPendingReviewModalVisible,
    close: closeMarkBillingStatusPendingReviewModal,
  } = useModal();

  const {
    visible: bulkUploadDocumentsModalVisible,
    show: showBulkUploadDocumentsModalVisible,
    close: closeBulkUploadDocumentsModalVisible,
  } = useModal();

  const {
    visible: bulkAddBridgeCodesModalVisible,
    show: showBulkAddBridgeCodesModalVisible,
    close: closeBulkAddBridgeCodesModalVisible,
  } = useModal();

  const {
    visible: bulkUpdatePrenoticeShipmentsModalVisible,
    show: showBulkUpdatePrenoticeShipmentsModalVisible,
    close: closeBulkUpdatePrenoticeShipmentsModalVisible,
  } = useModal();

  const handlePendingReviewModalUpdate = (updates) => {
    closeMarkBillingStatusPendingReviewModal();
    onBillingStatusPendingReviewUpdate(updates);
  };

  const handleBulkAction = (bulkAction) => {
    const {
      CHANGE_SHIPPER,
      CHANGE_CARRIER,
      ASSIGN_DRIVER,
      UPDATE_CARRIER_COST,
      SEND_DELIVERY_ORDER,
      BULK_ACCESSORIAL,
      SEND_BILL_OF_LADING,
      CHANGE_CONSIGNEE,
      SEND_RATE_CONFIRMATION,
      ADD_YARDS_IN_BULK,
      EVALUATE_BILLING_STATUS,
      CLOSE_SHIPMENT,
      OVERRIDE_BILLING_STATUS,
      MARK_BILLING_STATUS_PENDING_REVIEW,
      BULK_UPLOAD_DOCUMENTS,
      UPDATE_PRENOTICE_TO_FINAL,
      UPDATE_FINAL_TO_PRENOTICE,
      BULK_ADD_BRIDGE_CODES,
    } = massUpdateActions;
    switch (bulkAction) {
      case CHANGE_SHIPPER:
        showChangeShipperModal();
        break;
      case CHANGE_CARRIER:
        showChangeCarrierModal();
        break;
      case SEND_DELIVERY_ORDER:
        showBulkUpdateSendDOModal();
        break;
      case UPDATE_CARRIER_COST:
        showBulkUpdateCarrierCostModal();
        break;
      case BULK_ACCESSORIAL:
        showBulkAddAccessorialModal();
        break;
      case SEND_BILL_OF_LADING:
        showBulkBOLGenerationModal();
        break;
      case CHANGE_CONSIGNEE:
        showChangeConsigneeModal();
        break;
      case SEND_RATE_CONFIRMATION:
        showSendRateConfirmationModal();
        break;
      case ADD_YARDS_IN_BULK:
        showBulkAddYardsModal();
        break;
      case EVALUATE_BILLING_STATUS:
        showEvaluateBillingStatusConfirmationModal();
        break;
      case CLOSE_SHIPMENT:
        showBulkCloseModal();
        break;
      case OVERRIDE_BILLING_STATUS:
        showOverrideBillingStatusModal();
        break;
      case MARK_BILLING_STATUS_PENDING_REVIEW:
        showMarkBillingStatusPendingReviewModalVisible();
        break;
      case ASSIGN_DRIVER:
        showAssignDriverModal();
        break;
      case BULK_UPLOAD_DOCUMENTS:
        showBulkUploadDocumentsModalVisible();
        break;
      case BULK_ADD_BRIDGE_CODES:
        showBulkAddBridgeCodesModalVisible();
        break;
      case UPDATE_PRENOTICE_TO_FINAL:
      case UPDATE_FINAL_TO_PRENOTICE:
        showBulkUpdatePrenoticeShipmentsModalVisible();
        break;
      default:
        setBulkAction(bulkAction);
        showBulkEditModal();
        break;
    }
  };

  const notDrayage = view !== 'drayage';
  const overrideBillingStatusEnabled = SessionStore.isBillingStatusAdmin();
  const canChangeBillingStatus =
    SessionStore.isBillingStatusAdmin() ||
    SessionStore.isBillingStatusManager();
  const billingStatusPendingReviewEnabled =
    canChangeBillingStatus || SessionStore.isBillingStatusUser();
  const allArePreArrivalShipments = (selectedRows || [])?.every(
    (s) => s?.pre_arrival_shipment,
  );
  const disableFinalToPrenotice =
    !allArePreArrivalShipments ||
    selectedRows.some(
      (s) =>
        s?.shipment_pre_arrival_status !== 'final' &&
        s?.shipment_pre_arrival_status !== 'converted_final',
    );
  const disablePrenoticeToFinal =
    !allArePreArrivalShipments ||
    selectedRows.some((s) => s?.shipment_pre_arrival_status !== 'pre_notice');

  const showBillingStatusToggle = useMemo(() => {
    return (
      canChangeBillingStatus &&
      selectedRows.length > 0 &&
      selectedRows.every((item) => {
        return (
          item.billing_status_state_billing_status === PENDING_REVIEW &&
          (item.pause_automated_billing_evaluation_reason_codes?.length ??
            0) === 0
        );
      })
    );
  }, [selectedRows, canChangeBillingStatus]);

  const consigneeAuthorized = isAuthorizedToRender(
    { excludedRegions: [REGIONS.AP] },
    auth || {},
  );

  const actions = useMemo(() => {
    const list = [
      // Assignments
      {
        id: 'assign_driver',
        label: t('ASSIGN_DRIVER'),
        category: CATEGORIES.ASSIGNMENTS,
        action: massUpdateActions.ASSIGN_DRIVER,
        dataTestId: 'assignDriverButton',
        visible: true,
        disabled: changeCarrierDisabled,
      },
      {
        id: 'change_carrier',
        label: t('ASSIGN_CARRIER'),
        category: CATEGORIES.ASSIGNMENTS,
        action: massUpdateActions.CHANGE_CARRIER,
        dataTestId: 'changeCarrierButton',
        visible: true,
        disabled: changeCarrierDisabled,
      },
      {
        id: 'change_consignee',
        label: t('CHANGE_CONSIGNEE'),
        category: CATEGORIES.ASSIGNMENTS,
        action: massUpdateActions.CHANGE_CONSIGNEE,
        dataTestId: 'changeConsigneeButton',
        visible: consigneeAuthorized,
        disabled: notDrayage,
      },
      {
        id: 'change_shipper',
        label: t('CHANGE_SHIPPER'),
        category: CATEGORIES.ASSIGNMENTS,
        action: massUpdateActions.CHANGE_SHIPPER,
        dataTestId: 'changeShipperButton',
        visible: true,
        disabled: changeShipperDisabled,
      },

      // Date & Status Updates
      {
        id: 'edit_arrival',
        label: t('UPDATE_ARRIVAL'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.EDIT_ARRIVAL,
        dataTestId: 'editArrivalButton',
        visible: true,
        disabled: arrivedActionDisabled,
      },
      {
        id: 'update_appointment',
        label: t('UPDATE_APPOINTMENT_WINDOW'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.UPDATE_APPOINTMENT_WINDOW,
        dataTestId: 'updateAppointmentButton',
        visible: true,
      },
      {
        id: 'edit_discharge',
        label: t('UPDATE_DISCHARGE'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.EDIT_DISCHARGE,
        dataTestId: 'editDischargeButton',
        visible: true,
        disabled: notDrayage,
      },
      {
        id: 'update_earliest_get_empty',
        label: t('UPDATE_EARLIEST_EMPTY'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.UPDATE_EARLIEST_GET_EMPTY,
        dataTestId: 'updateEarliestGetEmptyButton',
        visible: true,
        disabled: notDrayage,
      },
      {
        id: 'update_empty_ready_for_pickup',
        label: t('UPDATE_EMPTY_READY_FOR_PICKUP'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.UPDATE_EMPTY_READY_FOR_PICKUP,
        dataTestId: 'updateEmptyReadyForPickupButton',
        visible: true,
        disabled: notDrayage,
      },
      {
        id: 'update_earliest_return',
        label: t('UPDATE_EARLIEST_RETURN'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.UPDATE_EARLIEST_RETURN,
        dataTestId: 'updateEarliestReturnButton',
        visible: true,
        disabled: notDrayage,
      },
      {
        id: 'edit_estimated_arrival',
        label: t('UPDATE_ETA'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.EDIT_ESTIMATED_ARRIVAL,
        dataTestId: 'editEstimatedArrivalButton',
        visible: true,
        disabled: notDrayage,
      },
      {
        id: 'update_empty_returned_date',
        label: t('UPDATE_EMPTY_RETURN'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.UPDATE_EMPTY_RETURN,
        dataTestId: 'updateEmptyReturnedDateButton',
        visible: true,
        disabled: notDrayage,
      },
      {
        id: 'update_last_cut',
        label: t('UPDATE_LAST_CUT_DATE'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.UPDATE_LAST_CUT,
        dataTestId: 'updateLastCutButton',
        visible: true,
        disabled: notDrayage,
      },
      {
        id: 'edit_last_free_day',
        label: t('UPDATE_TERMINAL_LFD'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.EDIT_LAST_FREE_DAY,
        dataTestId: 'editLastFreeDayButton',
        visible: true,
        disabled: notDrayage,
      },
      {
        id: 'update_eta_to_final_rail_facility',
        label: t('ETA_TO_FINAL_RAIL_FACILITY'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.EDIT_ETA_TO_FINAL_RAIL_FACILITY,
        dataTestId: 'updateEtaToFinalRailFacility',
        visible: true,
      },
      {
        id: 'update_rail_lfd',
        label: t('RAIL_LFD_ACTION'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.EDIT_LFD_AT_RAIL_FACILITY,
        dataTestId: 'updateRailLfd',
        visible: true,
      },
      {
        id: 'update_prenotice_to_final',
        label: t('UPDATE_PRENOTICE_TO_FINAL'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.UPDATE_PRENOTICE_TO_FINAL,
        dataTestId: 'updatePrenoticeToFinal',
        visible: featureEnabled(CONFIGURED_FEATURES.AMAZON_PRE_ARRIVAL),
        disabled: disablePrenoticeToFinal,
        disabledText: t('SHIPMENTS_NOT_ELIGIBLE_MSG'),
      },
      {
        id: 'update_final_to_prenotice',
        label: t('UPDATE_FINAL_TO_PRENOTICE'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.UPDATE_FINAL_TO_PRENOTICE,
        dataTestId: 'updateFinalToPrenotice',
        visible: featureEnabled(CONFIGURED_FEATURES.AMAZON_PRE_ARRIVAL),
        disabled: disableFinalToPrenotice,
        disabledText: t('SHIPMENTS_NOT_ELIGIBLE_MSG'),
      },
      {
        id: 'close_shipment',
        label: t('CLOSE_SHIPMENT'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.CLOSE_SHIPMENT,
        dataTestId: 'closeShipment',
        visible: SessionStore.isShipmentClosureAdmin(),
        disabled: notDrayage,
      },
      {
        id: 'complete_stop',
        label: t('COMPLETE_STOP'),
        category: CATEGORIES.DATE_STATUS,
        action: massUpdateActions.COMPLETE_STOP,
        dataTestId: 'completeStopButton',
        visible: true,
      },

      // Billing
      {
        id: 'update_carrier_cost',
        label: firstLetterTitlization(t('UPDATE_CARRIER_COST')),
        category: CATEGORIES.BILLING,
        action: massUpdateActions.UPDATE_CARRIER_COST,
        dataTestId: 'updateCarrierCostButton',
        visible: true,
      },
      {
        id: 'evaluate_billing_status',
        label: t('EVALUATE_BILLING_STATUS'),
        category: CATEGORIES.BILLING,
        action: massUpdateActions.EVALUATE_BILLING_STATUS,
        dataTestId: 'evaluateBillingStatusButton',
        visible: true,
      },
      {
        id: 'override_billing_status',
        label: t('OVERRIDE_BILLING_STATUS'),
        category: CATEGORIES.BILLING,
        action: massUpdateActions.OVERRIDE_BILLING_STATUS,
        dataTestId: 'overrideBillingStatus',
        visible: overrideBillingStatusEnabled,
        disabled: notDrayage,
      },
      {
        id: 'mark_billing_status_pending_review',
        label: t('MARK_BILLING_STATUS_PENDING_REVIEW'),
        category: CATEGORIES.BILLING,
        action: massUpdateActions.MARK_BILLING_STATUS_PENDING_REVIEW,
        dataTestId: 'markBillingStatusPendingReview',
        visible: billingStatusPendingReviewEnabled,
        disabled: notDrayage,
      },
      {
        id: 'update_review_date',
        label: `${t('UPDATE')} ${t('REVIEW_DATE')}`,
        category: CATEGORIES.BILLING,
        action: massUpdateActions.UPDATE_REVIEW_DATE,
        dataTestId: 'updateReviewDate',
        visible: true,
      },
      {
        id: 'update_accounting_review_date',
        label: `${t('UPDATE')} ${t('ACCOUNTING_REVIEW_DATE')}`,
        category: CATEGORIES.BILLING,
        action: massUpdateActions.UPDATE_ACCOUNTING_REVIEW_DATE,
        dataTestId: 'updateAccountingReviewDate',
        visible: true,
      },

      // Send & Notifications
      {
        id: 'send_bill_of_lading',
        label: t('SEND_BILL_OF_LADING'),
        category: CATEGORIES.SEND,
        action: massUpdateActions.SEND_BILL_OF_LADING,
        dataTestId: 'sendBillOfLadingButton',
        visible: true,
      },
      {
        id: 'send_delivery_order',
        label: t('SEND_DO'),
        category: CATEGORIES.SEND,
        action: massUpdateActions.SEND_DELIVERY_ORDER,
        dataTestId: 'sendDeliveryOrderButton',
        visible: true,
      },
      {
        id: 'send_rate_confirmation',
        label: t('SEND_RATE_CONFIRMATION'),
        category: CATEGORIES.SEND,
        action: massUpdateActions.SEND_RATE_CONFIRMATION,
        dataTestId: 'sendRateConfirmationButton',
        visible: true,
      },

      // Bulk Operations
      {
        id: 'bulk_accessorial',
        label: t('BULK_ACCESSORIALS'),
        category: CATEGORIES.BULK,
        action: massUpdateActions.BULK_ACCESSORIAL,
        dataTestId: 'bulkAccessorialButton',
        visible: true,
      },
      {
        id: 'bulk_apply_admin_notes',
        label: firstLetterTitlization(t('BULK_APPLY_ADMIN_NOTES')),
        category: CATEGORIES.BULK,
        action: massUpdateActions.BULK_APPLY_ADMIN_NOTES,
        dataTestId: 'bulkApplyNotesButton',
        visible: true,
      },
      {
        id: 'bulk_apply_special_instructions',
        label: firstLetterTitlization(t('BULK_APPLY_SPECIAL_INSTRUCTIONS')),
        category: CATEGORIES.BULK,
        action: massUpdateActions.BULK_APPLY_SPECIAL_INSTRUCTIONS,
        dataTestId: 'bulkApplySpecialInstructionsButton',
        visible: true,
      },
      {
        id: 'bulk_add_tags',
        label: firstLetterTitlization(t('BULK_ADD_TAGS')),
        category: CATEGORIES.BULK,
        action: massUpdateActions.BULK_ADD_TAGS,
        dataTestId: 'bulkAddTagsButton',
        visible: true,
      },
      {
        id: 'bulk_clear_tags',
        label: firstLetterTitlization(t('BULK_CLEAR_TAGS')),
        category: CATEGORIES.BULK,
        action: massUpdateActions.BULK_CLEAR_TAGS,
        dataTestId: 'bulkClearTags',
        visible: true,
      },
      {
        id: 'bulk_add_reference_numbers',
        label: firstLetterTitlization(t('BULK_ADD_REFERENCE_NUMBERS')),
        category: CATEGORIES.BULK,
        action: massUpdateActions.BULK_ADD_REFERENCE_NUMBERS,
        dataTestId: 'bulkAddRefNumbers',
        visible: true,
        disabled: !isSameShipperOrMasterShipper,
      },
      {
        id: 'bulk_add_bridge_codes',
        label: t('BULK_ADD_BRIDGE_CODES'),
        category: CATEGORIES.BULK,
        action: massUpdateActions.BULK_ADD_BRIDGE_CODES,
        dataTestId: 'bulkAddBridgeCodes',
        visible:
          !notDrayage &&
          featureEnabled(CONFIGURED_FEATURES.BULK_MARKET_EXCEPTIONS),
      },
      {
        id: 'bulk_add_terminal_holds',
        label: t('BULK_ADD_TERMINAL_HOLDS'),
        category: CATEGORIES.BULK,
        action: massUpdateActions.BULK_ADD_TERMINAL_HOLDS,
        dataTestId: 'bulkAddTerminalHolds',
        visible: !notDrayage,
      },
      {
        id: 'bulk_apply_pre_notice_accessorials',
        label: t('BULK_APPLY_PRE_NOTICE_ACCESSORIALS'),
        category: CATEGORIES.BULK,
        action: massUpdateActions.BULK_APPLY_PRE_NOTICE_ACCESSORIALS,
        dataTestId: 'bulkApplyPreNoticeAccessorials',
        visible: !notDrayage,
      },
      {
        id: 'add_yards_in_bulk',
        label: t('ADD_YARDS_IN_BULK'),
        category: CATEGORIES.BULK,
        action: massUpdateActions.ADD_YARDS_IN_BULK,
        dataTestId: 'addYardsInBulkButton',
        visible: !isProd(),
        disabled: notDrayage || !selectedShipmentsForYards.length,
      },
      {
        id: 'bulk_upload_documents',
        label: t('BULK_UPLOAD_DOCUMENTS'),
        category: CATEGORIES.BULK,
        action: massUpdateActions.BULK_UPLOAD_DOCUMENTS,
        dataTestId: 'bulkUploadDocuments',
        visible: true,
        disabled: bulkUploadDocumentsDisabled,
        disabledText: t('BULK_UPLOAD_DISABLED_DOCUMENTS_MESSAGE'),
      },
    ];
    return list.filter((a) => a.visible);
  }, [
    t,
    consigneeAuthorized,
    notDrayage,
    arrivedActionDisabled,
    changeCarrierDisabled,
    changeShipperDisabled,
    bulkUploadDocumentsDisabled,
    isSameShipperOrMasterShipper,
    overrideBillingStatusEnabled,
    billingStatusPendingReviewEnabled,
    disableFinalToPrenotice,
    disablePrenoticeToFinal,
    selectedShipmentsForYards,
  ]);

  const visibleActions = useMemo(() => {
    const byTab =
      activeTab === 'all'
        ? actions
        : actions.filter((a) => a.category === activeTab);
    const q = search.trim().toLowerCase();
    if (!q) return byTab;
    return byTab.filter((a) => a.label.toLowerCase().includes(q));
  }, [actions, activeTab, search]);

  const groupedActions = useMemo(() => {
    const groups = {};
    visibleActions.forEach((a) => {
      if (!groups[a.category]) groups[a.category] = [];
      groups[a.category].push(a);
    });
    return groups;
  }, [visibleActions]);

  // @cargomatic/ui CommandItem highlights when its `index` prop matches the
  // Downshift-tracked highlightedIndex. Indices must be globally unique across
  // groups, otherwise items at the same position in different groups all
  // highlight at once.
  const globalIndexById = useMemo(() => {
    const map = new Map();
    visibleActions.forEach((a, i) => map.set(a.id, i));
    return map;
  }, [visibleActions]);

  const runAction = (action) => {
    setOpen(false);
    handleBulkAction(action.action);
  };

  return (
    <>
      <TooltipProvider>
        <Popover
          // modal opts the popover out of any parent Dialog's pointer-events
          // lock so wheel scrolling works inside the list.
          // See https://github.com/dip/cmdk/issues/272
          modal
          open={open && !disabled}
          onOpenChange={(next) => {
            if (disabled) return;
            setOpen(next);
            if (!next) {
              setActiveTab('all');
              setSearch('');
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              data-testid="MenuButton-81d20177"
            >
              {t('ACTIONS')}
              <ChevronDown />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[420px] p-0" align="end">
            <Command key={open ? 'open' : 'closed'}>
              <CommandInput
                placeholder={`${titleize(t('SEARCH'))}...`}
                onValueChange={setSearch}
              />
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <div className="overflow-x-auto">
                  <TabsList>
                    <TabsTrigger value="all">{titleize(t('ALL'))}</TabsTrigger>
                    {CATEGORY_ORDER.map((cat) => {
                      if (!actions.some((a) => a.category === cat)) return null;
                      const Icon = CATEGORY_ICONS[cat];
                      return (
                        <TabsTrigger key={cat} value={cat}>
                          <Icon
                            className={`mr-1.5 h-3.5 w-3.5 ${CATEGORY_TAB_ICON_COLORS[cat]}`}
                          />
                          <span className="whitespace-nowrap">
                            {CATEGORY_LABELS[cat]}
                          </span>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </div>
              </Tabs>
              <CommandList className="max-h-[360px]">
                {visibleActions.length === 0 && (
                  <CommandEmpty>{t('NO_RESULTS_TO_DISPLAY')}</CommandEmpty>
                )}
                {CATEGORY_ORDER.map((cat) => {
                  const items = groupedActions[cat];
                  if (!items || !items.length) return null;
                  const Icon = CATEGORY_ICONS[cat];
                  return (
                    <CommandGroup
                      key={cat}
                      heading={
                        <div className="flex items-center">
                          <span
                            className={`mr-2 inline-flex h-5 w-5 items-center justify-center rounded ${CATEGORY_ICON_TINTS[cat]}`}
                          >
                            <Icon className="h-3 w-3" />
                          </span>
                          <span>{CATEGORY_LABELS[cat]}</span>
                        </div>
                      }
                    >
                      {items.map((a) => (
                        <ActionItem
                          key={a.id}
                          action={a}
                          index={globalIndexById.get(a.id)}
                          onRun={runAction}
                        />
                      ))}
                    </CommandGroup>
                  );
                })}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </TooltipProvider>
      {bulkAddBridgeCodesModalVisible && (
        <BulkShipmentBridges
          isOpen={bulkAddBridgeCodesModalVisible}
          shipments={selectedRows.map((j) => ({
            shipper: {
              id: j.shipper_oid || undefined,
            },
            ...j,
          }))}
          onClose={closeBulkAddBridgeCodesModalVisible}
        />
      )}
      {bulkUploadDocumentsModalVisible && (
        <BulkUploadDocumentsModel
          isOpen={bulkUploadDocumentsModalVisible}
          shipments={selectedRows.map((j) => ({
            shipper: {
              id: j.shipper_oid || undefined,
            },
            ...j,
          }))}
          onClose={closeBulkUploadDocumentsModalVisible}
        />
      )}
      {bulkEditModalVisible && (
        <BulkEditModal
          show
          action={bulkAction}
          onHide={closeBulkEditModal}
          shipments={selectedShipments}
        />
      )}
      {changeShipperModalVisible && (
        <ChangeUserModal
          jobs={selectedRows}
          show={changeShipperModalVisible}
          type="shipper"
          onSave={(shipper, selectedJobs) =>
            ShipmentsActionCreators.updateShipper(shipper, selectedJobs)
          }
          onHide={closeChangeShipperModal}
        />
      )}
      {assignDriverModalVisible && (
        <AssignDriverModal
          shipments={selectedRows.map((j) => ({
            shipper: {
              id: j.shipper_oid || undefined,
            },
            ...j,
          }))}
          shipment={_.first(selectedRows)}
          show={assignDriverModalVisible}
          showDrayageSelectionForShorthaul
          hideDriverSuggestions
          onAssignDriver={(shipments, user) => {
            let userId = '';
            if (user?._type === 'Driver') {
              userId = user?._id;
            } else {
              userId = user?.default_driver;
            }
            ShipmentsActionCreators.bulkAssignDriver(userId, shipments);
          }}
          onHide={closeAssignDriverModal}
        />
      )}
      {changeCarrierModalVisible && (
        <ChangeUserModal
          jobs={selectedRows.map((j) => ({
            shipper: {
              id: j.shipper_oid || undefined,
            },
            ...j,
          }))}
          show={changeCarrierModalVisible}
          type="carrier"
          onSave={(carrier, selectedJobs) =>
            ShipmentsActionCreators.updateCarrier(carrier, selectedJobs)
          }
          onAssignBrokerCarrier={(_shipment, driver) => {
            closeChangeCarrierModal();
            setBrokerCarrierDriver(driver);
            showConfirmAssignCarrierModal();
          }}
          onHide={closeChangeCarrierModal}
        />
      )}
      <ConfirmAssignCarrierModal
        isOpen={confirmAssignCarrierModalVisible}
        brokerCarrierDriver={brokerCarrierDriver}
        shipment={selectedRows.map((j) => ({
          shipper: {
            id: j.shipper_oid || undefined,
          },
          ...j,
        }))}
        onConfirm={(shipments, carrier) => {
          ShipmentsActionCreators.updateCarrier(carrier, shipments);
          closeConfirmAssignCarrierModal();
        }}
        onClose={closeConfirmAssignCarrierModal}
      />
      {changeConsigneeModalVisible && (
        <ChangeUserModal
          jobs={selectedRows}
          show={changeConsigneeModalVisible}
          type="consignee"
          onSave={(consignee, selectedJobs) =>
            ShipmentsActionCreators.updateConsignee(consignee, selectedJobs)
          }
          onHide={closeChangeConsigneeModal}
        />
      )}
      {bulkAddAccessorialModalVisible && (
        <BulkAddAccessorialsModal
          orderId={null}
          show={bulkAddAccessorialModalVisible}
          shipmentIds={(selectedShipmentsForAccessorials || []).map(
            (s) => s._id,
          )}
          onHide={closeBulkAddAccessorialModal}
          edit={false}
        />
      )}
      {bulkUpdateCarrierCostModalVisible && (
        <BulkUpdateCarrierCostModal
          show={bulkUpdateCarrierCostModalVisible}
          shipments={selectedShipments}
          onHide={closeBulkUpdateCarrierCostModal}
          includeAllJobs
        />
      )}
      {bulkUpdateSendDOModalVisible && (
        <BulkUpdateSendDOModal
          title={t('SEND_DELIVERY_ORDER')}
          send_do
          show={bulkUpdateSendDOModalVisible}
          shipments={selectedShipments}
          onHide={closeBulkUpdateSendDOModal}
        />
      )}
      {bulkBOLGenerationModalVisible && (
        <BulkBOLGenerationModal
          shipments={selectedShipments}
          onHide={closeBulkBOLGenerationModal}
        />
      )}
      {sendRateConfirmationModalVisible && (
        <BulkGenerateRateConfirmation
          showModal={sendRateConfirmationModalVisible}
          selectedShipments={selectedShipments}
          onHide={closeSendRateConfirmationModal}
        />
      )}
      {bulkAddYardsModalVisible && (
        <BulkAddYardsModal
          showModal={bulkAddYardsModalVisible}
          selectedShipmentsData={selectedShipmentsForYards}
          onHide={closeBulkAddYardsModal}
        />
      )}
      {evaluateBillingStatusConfirmationModalVisible && (
        <ConfirmationModal
          dataTestId="ConfirmationModal-d70ec431"
          open={evaluateBillingStatusConfirmationModalVisible}
          onHide={closeEvaluateBillingStatusConfirmationModal}
          title={t('EVALUATE_BILLING_STATUS')}
          description={t('EVALUATE_BILLING_STATUS_CONFIRMATION', {
            count: selectedShipments.length,
          })}
          onConfirm={async () => {
            try {
              await evaluateBillingStatus(selectedShipments);
              showToast(t('EVALUATE_BILLING_STATUS_SUCCESS'));
            } catch (e) {
              console.error(e);
              showToast(t('EVALUATE_BILLING_STATUS_FAILED'), '', 'error');
            }
          }}
        />
      )}
      {bulkCloseModalVisible && (
        <ConfirmationModal
          dataTestId="ConfirmationModal-39916d90"
          open={bulkCloseModalVisible}
          title={t('CLOSE_SHIPMENT')}
          description={
            <Alert className="mt-4">
              This will by-pass all workflows; are you sure you want to close
              these shipments?
            </Alert>
          }
          onHide={closeBulkCloseModal}
          onConfirm={async () => {
            try {
              const res = await bulkCloseJobs(
                selectedRows.map((row) => row.id),
              );
              const hasErrors = Object.values(res?.entity?.results).some(
                (res) => res.isError,
              );
              const hasSuccesses = Object.values(res?.entity?.results).some(
                (res) => !res.isError,
              );
              if (hasErrors) {
                showToast(
                  'There were some shipment close failures',
                  '',
                  'error',
                );
              } else if (hasSuccesses) {
                showToast(
                  'The selected shipments have been closed successfully!',
                );
              }
            } catch (e) {
              console.error(e);
              showToast(
                'There were problems closing your shipments',
                '',
                'error',
              );
            }
          }}
        />
      )}
      {overrideBillingStatusEnabled && overrideBillingStatusModalVisible && (
        <OverrideBillingStatusModal
          shipmentIds={selectedShipments}
          open={overrideBillingStatusModalVisible}
          onHide={closeOverrideBillingStatusModal}
        />
      )}
      {billingStatusPendingReviewEnabled &&
        markBillingStatusPendingReviewModalVisible && (
          <BillingStatusPendingReviewModal
            shipmentIds={selectedShipments}
            showStatusToggle={showBillingStatusToggle}
            reasonCodes={
              showBillingStatusToggle
                ? selectedRows?.[0]
                    ?.pause_automated_billing_evaluation_reason_codes
                : []
            }
            note={
              showBillingStatusToggle
                ? selectedRows?.[0]
                    ?.pause_automated_billing_evaluation_reason_code
                : ''
            }
            open={markBillingStatusPendingReviewModalVisible}
            onHide={closeMarkBillingStatusPendingReviewModal}
            onSuccess={handlePendingReviewModalUpdate}
          />
        )}
      {bulkUpdatePrenoticeShipmentsModalVisible && (
        <UpdatePrenoticeShipmentStatusModal
          status={
            selectedRows.every(
              (s) =>
                s?.shipment_pre_arrival_status === 'final' ||
                s?.shipment_pre_arrival_status === 'converted_final',
            )
              ? 'pre_arrival'
              : 'final'
          }
          orders={_.uniq(selectedRows?.map((s) => s?.order_oid))}
          shipmentCount={(selectedRows || []).length}
          open={bulkUpdatePrenoticeShipmentsModalVisible}
          onHide={closeBulkUpdatePrenoticeShipmentsModalVisible}
        />
      )}
    </>
  );
}

export default BulkActionsDropdown;
